/* ============================================================================
   ProfileGate - the trainer step (name and level), on request.
   ----------------------------------------------------------------------------
   Renders the AuthModal in mandatoryMode (two typed fields on one row:
   trainer name and level) when the step is due for the signed-in account
   (lib/trainerSetup.trainerStepKind):

     - it was asked for on this device (lib/profileSetupRequest): AuthProvider
       asks the moment an account with no trainer name signs up or in, and
       Live Scan's "Add my trainer name" asks for one that put it off;
     - or the account has never TYPED its name and level. Owner decision,
       2026-10-02: the profile screenshots and what was read off them were
       removed, and every existing account is asked the next time it opens
       Nexus Dex. Those trainers see the "confirm" face, prefilled with what
       the account holds. The server remembers the answer, so it is asked once
       per account, not once per phone.
   "Not now" closes it until the app is next opened.

   Until 2026-10-02 this step was a screenshot of the Pokémon GO profile, read
   by OCR. That is gone (lib/trainerSetup).

   The gate sits outside <RequireAuth> in App.tsx so it can render over any
   page (including / which is Profile). It does nothing on public auth routes
   (/login, /signup, /welcome, /auth/*) so users mid-OAuth don't see it
   flicker before the redirect lands.
   ============================================================================ */
import { useLocation } from "wouter";
import { useAuth } from "./AuthProvider";
import { AuthModal } from "./AuthModal";
import { useTrainerStepKind } from "@/lib/useTrainerStep";

// Public routes that should NEVER show the gate, even if account is partial.
// Keep aligned with RequireAuth.PUBLIC_ROUTES.
const GATE_BLOCKLIST = [
  "/welcome",
  "/login",
  "/signup",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify-email",
  "/portal/auth/forgot-password",
  "/portal/auth/reset-password",
  "/portal/auth/verify-email",
];

function isBlockedRoute(path: string): boolean {
  // The web portal gates itself (Portal.tsx's ProfileGateScreen renders the
  // same step); a second dialog over it would stack two of them.
  if (path === "/portal" || path.startsWith("/portal/") || path.startsWith("/portal?")) return true;
  return GATE_BLOCKLIST.some((p) => path === p || path.startsWith(p + "?"));
}

export function ProfileGate() {
  const { account, isSignedIn, isRestoring } = useAuth();
  const [loc] = useLocation();
  const stepKind = useTrainerStepKind(account);

  // Don't fire while we're hydrating a persisted session or on public routes.
  if (isRestoring) return null;
  if (isBlockedRoute(loc)) return null;
  if (!isSignedIn || !account) return null;

  // Asked for on this device, or owed by an account that has never typed its
  // name and level (and has not put it off since the app was opened).
  if (!stepKind) return null;

  // A dedicated AuthModal instance in mandatory mode, distinct from the global
  // one in App.tsx. The global one is only ever the sign in / sign up form and
  // closes itself the moment that succeeds, so the two never stack.
  // (The step brings its own backdrop, AuthModal's trainer-step-backdrop.)
  return <AuthModal mandatoryMode forceOpen deferrable stepKind={stepKind} />;
}
